import { n$2 } from '@/js/global/global'
import { formatMoney } from '@/js/modules/format-money'
import { getStorage, removeStorage, setStorage } from '@/js/global/local-storage-helpers'

const GEO_STORAGE_KEY = 'roc_cart_estimate_geo'
const GEO_TTL_MS = 24 * 60 * 60 * 1000
const GEO_ENDPOINT = 'https://api.ipgeolocation.io/ipgeo'
const CONTACT_PLACEHOLDER = {
  firstName: 'Estimate',
  lastName: 'Only',
  phone: '+12025550123'
}
const ADDRESS1_PLACEHOLDER = '1 Main St'
const STATE_ZIP_FALLBACK = {
  AL: '36104', AK: '99801', AZ: '85001', AR: '72201', CA: '95814', CO: '80202', CT: '06103',
  DE: '19901', DC: '20001', FL: '32301', GA: '30303', HI: '96813', ID: '83702', IL: '62701',
  IN: '46204', IA: '50309', KS: '66603', KY: '40601', LA: '70802', ME: '04330', MD: '21401',
  MA: '02108', MI: '48933', MN: '55102', MS: '39201', MO: '65101', MT: '59601', NE: '68508',
  NV: '89701', NH: '03301', NJ: '08608', NM: '87501', NY: '12207', NC: '27601', ND: '58501',
  OH: '43215', OK: '73102', OR: '97301', PA: '17101', RI: '02903', SC: '29201', SD: '57501',
  TN: '37219', TX: '78701', UT: '84111', VT: '05602', VA: '23219', WA: '98501', WV: '25301',
  WI: '53703', WY: '82001', PR: '00901'
}

const selectors = {
  shipping: '[data-estimate-shipping]',
  tax: '[data-estimate-tax]',
  total: '[data-estimate-total]',
  checkoutTotal: '[data-checkout-total]',
  progressOffers: '[data-progress-offers]',
  freeShippingBar: '[data-free-shipping-bar]'
}

const CART_FIELDS = `
  id
  cost { totalTaxAmount { amount } }
  deliveryGroups(first: 1) {
    nodes {
      id
      selectedDeliveryOption { handle }
      deliveryOptions { handle title estimatedCost { amount } }
    }
  }
`
const CREATE_CART = `mutation cartEstimateCreate($input: CartInput!) {
  cartCreate(input: $input) { cart { ${CART_FIELDS} } userErrors { message } }
}`
const SELECT_DELIVERY = `mutation cartEstimateSelectDelivery($cartId: ID!, $selected: [CartSelectedDeliveryOptionInput!]!) {
  cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $selected) { userErrors { message } }
}`
const PREPARE_CART = `mutation cartEstimatePrepare($cartId: ID!) {
  cartPrepareForCompletion(cartId: $cartId) {
    result { __typename ... on CartStatusReady { cart { ${CART_FIELDS} } } }
    userErrors { message }
  }
}`
const QUERY_CART = `query cartEstimateQuery($cartId: ID!) { cart(id: $cartId) { ${CART_FIELDS} } }`

// Single IP lookup shared by every CartEstimate instance for the lifetime of the page
let geoLookup = null

function reportError(context, error) {
  console.warn(`[cart-estimate] ${context}:`, error)
}

const toCents = amount => Math.round(parseFloat(amount) * 100)

function freeShippingThresholdCents(container) {
  const rate = window.Shopify?.currency?.rate || 1
  const dollarsToCents = value => Math.round(parseFloat(value) * rate * 100)

  const offers = n$2(selectors.progressOffers, container)
  if (offers) {
    const names = (offers.dataset.offerNames || '').split('|')
    const thresholds = (offers.dataset.offerThresholds || '').split('|')
    const index = names.findIndex((name, i) => (
      /shipping/i.test(name) && thresholds[i] && thresholds[i] !== '-' && !Number.isNaN(parseFloat(thresholds[i]))
    ))
    if (index !== -1) return dollarsToCents(thresholds[index])
  }

  const threshold = n$2(selectors.freeShippingBar, container)?.dataset.threshold
  if (threshold && !Number.isNaN(parseFloat(threshold))) return dollarsToCents(threshold)

  return null
}

function readConfig(container) {
  try {
    return JSON.parse(container.getAttribute('data-estimates') || 'null')
  } catch (error) {
    reportError('invalid data-estimates config, estimates disabled', error)
    return null
  }
}

function loadCustomerPrivacy() {
  const privacy = window.Shopify?.customerPrivacy
  if (typeof privacy?.analyticsProcessingAllowed === 'function') return Promise.resolve(privacy)
  if (typeof window.Shopify?.loadFeatures !== 'function') return Promise.resolve(null)
  return new Promise(resolve => {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      if (error) reportError('Customer Privacy API could not be loaded', error)
      resolve(error ? null : window.Shopify.customerPrivacy)
    })
  })
}

async function consentAllowed() {
  const privacy = await loadCustomerPrivacy()
  try {
    return Boolean(privacy?.analyticsProcessingAllowed())
  } catch (error) {
    reportError('could not read tracking consent, treating it as not granted', error)
    return false
  }
}

function usableLocation(location) {
  if (location?.countryCode !== 'US' || !location.provinceCode) return null
  const zip = location.zip || STATE_ZIP_FALLBACK[location.provinceCode]
  if (!zip) return null
  return { ...location, zip, address1: location.address1 || ADDRESS1_PLACEHOLDER }
}

function readGeoCache() {
  try {
    const cached = getStorage(GEO_STORAGE_KEY)
    if (cached?.ts && Date.now() - cached.ts < GEO_TTL_MS) return cached
  } catch (error) {
    reportError('discarding unreadable geolocation cache entry', error)
    removeStorage(GEO_STORAGE_KEY)
  }
  return null
}

function parseGeoResponse(data) {
  const countryCode = data.country_code2 || ''
  const provinceCode = String(data.state_code || '').split('-').pop().toUpperCase()
  let zip = String(data.zipcode || '').trim()
  if (countryCode === 'US') zip = zip.match(/^\d{5}/)?.[0] || ''
  return { countryCode, provinceCode, city: data.city || '', zip }
}

async function lookupGeoLocation(apiKey) {
  let location = null
  try {
    const response = await fetch(`${GEO_ENDPOINT}?apiKey=${encodeURIComponent(apiKey)}&fields=geo`)
    if (response.ok) {
      location = parseGeoResponse(await response.json())
    } else {
      reportError('geolocation lookup failed', `HTTP ${response.status}`)
    }
  } catch (error) {
    reportError('geolocation lookup failed', error)
  }

  setStorage(GEO_STORAGE_KEY, JSON.stringify({ ts: Date.now(), location }))
  return location
}

function geoLocation(apiKey) {
  geoLookup ??= lookupGeoLocation(apiKey)
  return geoLookup
}

export function CartEstimate(container) {
  const config = readConfig(container)
  const noop = { refresh() {}, unload() {} }
  if (!config?.enabled) return noop

  const endpoint = window.theme?.routes?.storefrontApi
  const cartUrl = `${window.theme?.routes?.cart?.base || '/cart'}.js`
  if (!endpoint) return noop

  const strings = config.strings || {}
  let sequence = 0
  let running = false
  let queued = false
  let lastResult = null

  const onConsent = () => refresh()
  document.addEventListener('visitorConsentCollected', onConsent)

  const money = cents => formatMoney(cents, window.theme.moneyFormat) + (config.currencySuffix || '')

  function setText(selector, text, state) {
    const node = n$2(selector, container)
    if (!node) return
    node.textContent = text
    if (state) {
      node.setAttribute('data-state', state)
    } else {
      node.removeAttribute('data-state')
    }
  }

  function freeShippingReached(cart) {
    const threshold = freeShippingThresholdCents(container)
    return threshold !== null && cart.total_price >= threshold
  }

  function renderPending(cart) {
    if (freeShippingReached(cart)) {
      setText(selectors.shipping, strings.free || 'Free', 'ready')
    } else {
      setText(selectors.shipping, strings.calculating || '…', 'pending')
    }
    setText(selectors.tax, strings.calculating || '…', 'pending')
  }

  function render(result, cart) {
    let total = cart.total_price

    let shippingCents = result?.shippingCents ?? null
    if (freeShippingReached(cart)) shippingCents = 0

    if (shippingCents !== null) {
      total += shippingCents
      setText(selectors.shipping, shippingCents === 0 ? (strings.free || 'Free') : money(shippingCents), 'ready')
    } else {
      setText(selectors.shipping, strings.unavailable || '', 'unavailable')
    }

    const taxCents = result?.taxCents ?? null
    if (taxCents !== null) {
      total += taxCents
      setText(selectors.tax, money(taxCents), 'ready')
    } else {
      setText(selectors.tax, strings.unavailable || '', 'unavailable')
    }

    setText(selectors.total, money(total))
    setText(selectors.checkoutTotal, money(total))
  }

  async function gql(query, variables) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables })
    })
    if (!response.ok) throw new Error(`Storefront API ${response.status}`)
    const payload = await response.json()
    if (payload.errors?.length) throw new Error(payload.errors[0].message)
    return payload.data
  }

  async function resolveLocation() {
    const customerAddress = window.theme?.customer?.address
    if (customerAddress) return usableLocation(customerAddress)

    const cached = readGeoCache()
    if (cached) return usableLocation(cached.location)

    if (!config.geoApiKey) return null
    if (config.requireConsent && !(await consentAllowed())) return null

    return usableLocation(await geoLocation(config.geoApiKey))
  }

  const sellingPlanId = item => item.selling_plan_allocation?.selling_plan?.id

  function cacheKey(cart, location) {
    const lines = cart.items.map(item => `${item.variant_id}:${item.quantity}:${sellingPlanId(item) ?? ''}`)
    return `${lines.join('|')}@${location.countryCode}-${location.provinceCode}-${location.zip}@${cart.total_price}`
  }

  async function estimate(cart, location) {
    const lines = cart.items.map(item => {
      const line = {
        merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: item.quantity
      }
      const planId = sellingPlanId(item)
      if (planId) line.sellingPlanId = `gid://shopify/SellingPlan/${planId}`
      return line
    })

    const deliveryAddress = {
      countryCode: location.countryCode,
      provinceCode: location.provinceCode,
      city: location.city || '',
      zip: location.zip,
      address1: location.address1,
      ...CONTACT_PLACEHOLDER
    }

    const created = await gql(CREATE_CART, {
      input: {
        lines,
        buyerIdentity: { countryCode: location.countryCode },
        delivery: { addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress } }] }
      }
    })
    const errors = created.cartCreate.userErrors
    if (errors?.length) throw new Error(errors[0].message)

    let shadowCart = created.cartCreate.cart
    const group = shadowCart.deliveryGroups.nodes[0]
    const options = group?.deliveryOptions ?? []
    if (!options.length) return { shippingCents: null, taxCents: null }

    const cheapest = options.reduce((best, option) => (
      toCents(option.estimatedCost.amount) < toCents(best.estimatedCost.amount) ? option : best
    ))

    if (group.selectedDeliveryOption?.handle !== cheapest.handle) {
      await gql(SELECT_DELIVERY, {
        cartId: shadowCart.id,
        selected: [{ deliveryGroupId: group.id, deliveryOptionHandle: cheapest.handle }]
      })
    }

    const prepared = await gql(PREPARE_CART, { cartId: shadowCart.id })
    shadowCart = prepared.cartPrepareForCompletion.result?.cart
      ?? (await gql(QUERY_CART, { cartId: shadowCart.id })).cart

    const tax = shadowCart?.cost?.totalTaxAmount
    return {
      shippingCents: toCents(cheapest.estimatedCost.amount),
      taxCents: tax ? toCents(tax.amount) : null
    }
  }

  async function run() {
    const mySequence = ++sequence
    const response = await fetch(cartUrl, { credentials: 'include' })
    if (!response.ok) return
    const cart = await response.json()
    if (mySequence !== sequence || !cart.item_count) return

    const location = await resolveLocation()
    if (mySequence !== sequence) return
    if (!location) {
      render(null, cart)
      return
    }

    const key = cacheKey(cart, location)
    if (lastResult?.key === key) {
      render(lastResult, cart)
      return
    }

    renderPending(cart)
    try {
      const result = await estimate(cart, location)
      if (mySequence !== sequence) return
      lastResult = { key, ...result }
      render(lastResult, cart)
    } catch (error) {
      reportError('shipping/tax estimate failed', error)
      if (mySequence !== sequence) return
      render(null, cart)
    }
  }

  function refresh() {
    if (!n$2(selectors.total, container)) return
    if (running) {
      queued = true
      return
    }
    running = true
    run().catch(error => reportError('could not refresh the estimate', error)).finally(() => {
      running = false
      if (queued) {
        queued = false
        refresh()
      }
    })
  }

  function unload() {
    document.removeEventListener('visitorConsentCollected', onConsent)
    sequence++
  }

  return { refresh, unload }
}
